"use client";

import classNames from "classnames";
import { type CSSProperties } from "react";
import { Avatar } from "@zapier/design-system";
import styles from "./Author.module.css";
import { SocialLinks } from "./SocialLinks";
import { AuthorLabel } from "./AuthorLabel";
import { AuthorMeta } from "./AuthorMeta";

type AuthorData = {
  /** The author's full name or company name */
  name: string;
  /** The author's job title */
  jobTitle?: string | null;
  /** The company name */
  company?: string | null;
  /** Avatar image object with URL */
  avatarImage?: {
    url?: string;
    alt?: string;
  } | null;
  /** URL for the company link */
  url?: string | null;
  /** LinkedIn URL */
  linkedInUrl?: string | null;
  /** Twitter URL */
  twitterUrl?: string | null;
  /** Facebook URL */
  facebookUrl?: string | null;
  /** Instagram URL */
  instagramUrl?: string | null;
  /** Display as company profile instead of person profile */
  isCompanyProfile?: boolean;
};

type AuthorProps = {
  /** Author data object */
  author: AuthorData;
  /** Optional label text to display above the author card. Defaults to "Created by" */
  label?: string;
  /** Custom CSS class */
  className?: string;
  /** Custom styles */
  style?: CSSProperties;
  /** Test ID for testing */
  testId?: string;
};

/**
 * Validates and normalizes a URL string
 * @param url - The URL to validate
 * @returns A valid URL string or undefined if invalid
 */
const validateUrl = (url: string | null | undefined): string | undefined => {
  if (!url || typeof url !== "string" || url.trim() === "") {
    return undefined;
  }

  const trimmedUrl = url.trim();

  try {
    // If URL doesn't start with protocol, add https://
    const urlToValidate =
      trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")
        ? trimmedUrl
        : `https://${trimmedUrl}`;

    // Use URL constructor to validate
    const validatedUrl = new URL(urlToValidate);

    // Only allow http and https protocols
    if (
      validatedUrl.protocol !== "https:" &&
      validatedUrl.protocol !== "http:"
    ) {
      return undefined;
    }

    return validatedUrl.toString();
  } catch {
    // Invalid URL format
    return undefined;
  }
};

export const Author = ({
  author,
  label,
  className,
  style,
  testId,
}: AuthorProps) => {
  const {
    name,
    jobTitle,
    company,
    avatarImage,
    url,
    linkedInUrl,
    twitterUrl,
    facebookUrl,
    instagramUrl,
    isCompanyProfile = false,
  } = author;
  // Transform individual social URLs to SocialLinks format with validation
  const socialLinks = {
    linkedin: validateUrl(linkedInUrl),
    twitter: validateUrl(twitterUrl),
    facebook: validateUrl(facebookUrl),
    instagram: validateUrl(instagramUrl),
  };

  // Validate the main company URL
  const validatedUrl = validateUrl(url);

  return (
    <div className={styles.authorWithLabel}>
      <AuthorLabel label={label ?? "Created by"} />
      <div
        className={classNames(styles.authorCard, className)}
        style={style}
        data-testid={testId}
      >
        {/* Avatar from design-system */}
        <div className={styles.avatar} data-testid="author-avatar">
          <Avatar
            name={name || "?"}
            url={avatarImage?.url ?? null}
            size="medium"
          />
        </div>

        {/* Author information */}
        <div className={styles.authorInfo}>
          <div className={styles.authorNameMeta}>
            <div className={styles.authorName}>{name}</div>
            <AuthorMeta
              title={jobTitle}
              company={company}
              url={validatedUrl}
              isCompanyProfile={isCompanyProfile}
            />
          </div>
          {Object.values(socialLinks).some(Boolean) && (
            <SocialLinks
              socialLinks={socialLinks}
              authorName={name}
              testId="social-links"
            />
          )}
        </div>
      </div>
    </div>
  );
};
